"use client";

import React from "react";
import { ArrowLeft, Calendar, User, Clock, Share2, Tag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-6 h-6 w-32 animate-pulse rounded-md bg-muted" />
        <div className="mb-4 h-12 w-3/4 animate-pulse rounded-md bg-muted" />
        <div className="mb-8 h-5 w-48 animate-pulse rounded-md bg-muted" />
        <div className="mb-10 aspect-16/9 w-full animate-pulse rounded-2xl bg-muted" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-5/6 animate-pulse rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-base text-destructive">No pudimos cargar esta publicación.</p>
        <Button variant="outline" onClick={refetch} className="mt-4">
          Intentar nuevamente
        </Button>
      </div>
    );
  }

  const post = posts?.[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h2 className="font-serif text-2xl font-semibold text-foreground">
          Artículo no encontrado
        </h2>
        <p className="mt-2 text-muted-foreground">
          La publicación que buscas no está disponible o ha cambiado de enlace.
        </p>
        <Button asChild className="mt-6">
          <Link to="/HomePage">Volver al inicio</Link>
        </Button>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("es-MX", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <article className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      {/* Top Navigation */}
      <div className="mb-8 flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild className="gap-2 text-muted-foreground hover:text-foreground">
          <Link to="/HomePage">
            <ArrowLeft className="size-4" />
            <span>Volver al inicio</span>
          </Link>
        </Button>

        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {post.categories.map((category, idx) => (
              <span
                key={category.id || idx}
                data-index={idx}
                className="inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground"
              >
                <Tag className="size-3" />
                {category.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Post Header */}
      <header className="mb-8 text-center sm:mb-10">
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          {post.title}
        </h1>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground sm:gap-6 sm:text-sm">
          {post.author?.name && (
            <div className="flex items-center gap-1.5">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}

          {formattedDate && (
            <div className="flex items-center gap-1.5">
              <Calendar className="size-4" />
              <time dateTime={post.date}>{formattedDate}</time>
            </div>
          )}

          <div className="flex items-center gap-1.5">
            <Clock className="size-4" />
            <span>Lectura de 4 min</span>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-2xl border border-border bg-muted/20 shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="aspect-16/9 w-full object-cover"
          />
        </div>
      )}

      {/* Post Content */}
      <div className="prose prose-stone max-w-none text-foreground leading-relaxed">
        {post.content ? (
          <div
            dangerouslySetInnerHTML={{ __html: post.content }}
            className="space-y-6 text-base text-foreground sm:text-lg [&>h2]:font-serif [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:text-foreground [&>h3]:font-serif [&>h3]:text-xl [&>h3]:font-medium [&>h3]:text-foreground [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic"
          />
        ) : post.excerpt ? (
          <p className="text-lg leading-relaxed text-foreground">{post.excerpt}</p>
        ) : (
          <p className="text-muted-foreground">El contenido de esta publicación estará disponible próximamente.</p>
        )}
      </div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-border pt-6">
          <span className="text-xs font-semibold text-muted-foreground">Etiquetas:</span>
          {post.tags.map((tag, idx) => (
            <span
              key={tag.id || idx}
              data-index={idx}
              className="rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Author Box */}
      {post.author && (
        <div className="mt-12 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
            {post.author.avatar?.["96"] ? (
              <img
                src={post.author.avatar["96"]}
                alt={post.author.name}
                loading="lazy"
                className="size-16 rounded-full border border-border object-cover"
              />
            ) : (
              <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <User className="size-8" />
              </div>
            )}
            <div>
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Escrito por
              </span>
              <h3 className="font-serif text-lg font-semibold text-card-foreground">
                {post.author.name}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Especialista en rituales botánicos y bienestar funcional en Spora.
              </p>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export default function Article() {
  const currentWPQueryParams =
    typeof wvcClient !== "undefined" && wvcClient.getCurrentWPQueryParams
      ? wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="w-full bg-background text-foreground"
    >
      <PostsProvider className="single-post-provider-wrapper" wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}